import { useState, useRef, useEffect } from 'react';
import {
  Wrench,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Database,
  Search,
  Cloud,
  Server,
  CheckCircle,
  AlertCircle,
  Key,
  Eye,
  EyeOff,
  RefreshCw,
} from 'lucide-react';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Tooltip } from '@/components/Tooltip';
import { StatusBadge } from '@/components/Badge';
import { useToast } from '@/contexts/ToastContext';
import { confirmDelete, showDeleteSuccess } from '@/utils/deleteConfirmation';

export function ToolsConfig() {
  const { showToast } = useToast();
  const [showSecrets, setShowSecrets] = useState(false);
  const [isAddingTool, setIsAddingTool] = useState(false);
  const [isAddingServer, setIsAddingServer] = useState(false);
  const [editingServer, setEditingServer] = useState<string | null>(null);
  const [testingServer, setTestingServer] = useState<string | null>(null);
  const testTimerRef = useRef<number | null>(null);

  const handleTestConnection = (serverId: string, serverName: string) => {
    setTestingServer(serverId);
    if (testTimerRef.current) {
      clearTimeout(testTimerRef.current);
    }
    testTimerRef.current = window.setTimeout(() => {
      setTestingServer(null);
      const latency = Math.floor(Math.random() * 200 + 100);
      showToast('success', `${serverName} Connection Test`, `Latency: ${latency}ms - Status: Connected`);
    }, 1500);
  };

  const handleDeleteServer = async (serverName: string) => {
    const confirmed = await confirmDelete({
      title: 'Delete Server?',
      itemName: serverName,
    });

    if (confirmed) {
      showDeleteSuccess(`${serverName} has been deleted successfully.`);
    }
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (testTimerRef.current) {
        clearTimeout(testTimerRef.current);
      }
    };
  }, []);

  const tools = [
    {
      id: '1',
      name: 'Web Search',
      type: 'search',
      description: 'Search the web for real-time information',
      status: 'active',
      usageCount: 2341,
    },
    {
      id: '2',
      name: 'Code Executor',
      type: 'execution',
      description: 'Execute Python code safely in sandbox',
      status: 'active',
      usageCount: 892,
    },
    {
      id: '3',
      name: 'Document Analyzer',
      type: 'analysis',
      description: 'Analyze and extract information from documents',
      status: 'active',
      usageCount: 1567,
    },
  ];

  // Actual 7 MCP servers from backend architecture
  const mcpServers = [
    {
      id: '1',
      category: 'Vector Database',
      name: 'MCP Pinecone',
      icon: '🌲',
      endpoint: 'pinecone.io/vectors',
      status: 'connected',
      type: 'vector-db',
      weight: 0.85,
      avgLatency: '210ms',
      uptime: '99.9%',
      description: 'Vector database for semantic search across learning materials',
    },
    {
      id: '2',
      category: 'Document Store',
      name: 'MCP PDF Content',
      icon: '📄',
      endpoint: 'internal/pdf-store',
      status: 'connected',
      type: 'documents',
      weight: 0.80,
      avgLatency: '180ms',
      uptime: '99.8%',
      description: 'PDF document content extraction and retrieval',
    },
    {
      id: '3',
      category: 'Learning Objectives',
      name: 'MCP OE Database',
      icon: '🎯',
      endpoint: 'internal/oe-db',
      status: 'connected',
      type: 'database',
      weight: 0.95,
      avgLatency: '95ms',
      uptime: '100%',
      description: 'Learning objectives (OE) database for curriculum mapping',
    },
    {
      id: '4',
      category: 'Legal & Compliance',
      name: 'MCP Regulations',
      icon: '⚖️',
      endpoint: 'internal/regulations-db',
      status: 'connected',
      type: 'database',
      weight: 1.00,
      avgLatency: '320ms',
      uptime: '99.7%',
      description: 'Financial regulations and compliance information',
    },
    {
      id: '5',
      category: 'Question Patterns',
      name: 'MCP Previous Exams',
      icon: '📝',
      endpoint: 'internal/exam-archive',
      status: 'connected',
      type: 'database',
      weight: 0.90,
      avgLatency: '150ms',
      uptime: '99.5%',
      description: 'Historical exam questions and patterns',
    },
    {
      id: '6',
      category: 'Terminology',
      name: 'MCP Glossary',
      icon: '📖',
      endpoint: 'internal/glossary',
      status: 'connected',
      type: 'database',
      weight: 0.70,
      avgLatency: '85ms',
      uptime: '100%',
      description: 'Financial terms and definitions glossary',
    },
    {
      id: '7',
      category: 'Official Questions',
      name: 'MCP ANBIMA API',
      icon: '🏛️',
      endpoint: 'api.anbima.com.br',
      status: 'warning',
      type: 'external-api',
      weight: 0.95,
      avgLatency: '450ms',
      uptime: '97.2%',
      description: 'Official ANBIMA certification exam questions',
    },
  ];

  const serverCategories = [
    {
      id: 'vector-db',
      name: 'Vector Databases',
      icon: <Database className="w-6 h-6" />,
      description: 'Semantic search via vector embeddings',
      color: 'blue',
      count: mcpServers.filter(s => s.type === 'vector-db').length,
    },
    {
      id: 'database',
      name: 'Internal Databases',
      icon: <Server className="w-6 h-6" />,
      description: 'Internal knowledge bases and data stores',
      color: 'purple',
      count: mcpServers.filter(s => s.type === 'database').length,
    },
    {
      id: 'documents',
      name: 'Document Stores',
      icon: <Search className="w-6 h-6" />,
      description: 'PDF and document content retrieval',
      color: 'green',
      count: mcpServers.filter(s => s.type === 'documents').length,
    },
    {
      id: 'external-api',
      name: 'External APIs',
      icon: <Cloud className="w-6 h-6" />,
      description: 'Third-party API integrations',
      color: 'orange',
      count: mcpServers.filter(s => s.type === 'external-api').length,
    },
  ];

  const toolTypes = [
    { value: 'search', label: 'Search Tool', icon: '🔍' },
    { value: 'analysis', label: 'Analysis Tool', icon: '📊' },
    { value: 'execution', label: 'Execution Tool', icon: '⚙️' },
    { value: 'data', label: 'Data Tool', icon: '💾' },
  ];

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb items={[
        { label: 'RAG System', path: '/rag' },
        { label: 'MCP Servers' }
      ]} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Tools & MCP Servers</h1>
          <p className="text-sm text-gray-600 mt-1">
            Configure tools and Model Context Protocol servers for agent use
          </p>
        </div>
        <Tooltip content={showSecrets ? "Hide API keys and credentials" : "Show API keys and credentials for all servers"}>
          <button
            onClick={() => setShowSecrets(!showSecrets)}
            className="btn-secondary flex items-center text-sm"
          >
            {showSecrets ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
            {showSecrets ? 'Hide' : 'Show'} Secrets
          </button>
        </Tooltip>
      </div>

      {/* Info Banner */}
      <div className="card bg-blue-50 border-blue-200">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-blue-900 mb-1">About MCP Servers (7 Knowledge Sources)</p>
            <p className="text-blue-700">
              Model Context Protocol (MCP) servers enable the Claude Query Analyzer agent to access specialized
              knowledge sources. Your Agentic RAG pipeline uses 7 MCP servers: Pinecone (vector search), PDF Content,
              OE Database (learning objectives), Regulations, Previous Exams, Glossary, and ANBIMA API. Each server
              has a weight (0.70-1.00) indicating trust level for hybrid reranking.
            </p>
          </div>
        </div>
      </div>

      {/* Tools Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Wrench className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Agent Tools</h2>
              <p className="text-sm text-gray-600">Tools that agents can use to perform actions</p>
            </div>
          </div>
          <Tooltip content="Add a new tool for agents to use in their workflows">
            <button
              onClick={() => setIsAddingTool(true)}
              className="btn-primary flex items-center text-sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Tool
            </button>
          </Tooltip>
        </div>

        {/* Add Tool Form */}
        {isAddingTool && (
          <div className="mb-6 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-orange-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Plus className="w-5 h-5 text-orange-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Add New Tool</h3>
                </div>
                <Tooltip content="Close form">
                  <button
                    onClick={() => setIsAddingTool(false)}
                    className="p-2 hover:bg-white/50 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </Tooltip>
              </div>
            </div>
            <form className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Tooltip content="The name of the tool as it will appear to agents">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Tool Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., Web Search"
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                    />
                  </div>
                </Tooltip>
                <Tooltip content="Category of tool functionality">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Tool Type <span className="text-red-500">*</span>
                    </label>
                    <select className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all">
                      {toolTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.icon} {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </Tooltip>
                <div className="col-span-2">
                  <Tooltip content="Explain what this tool does and when agents should use it">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        rows={2}
                        placeholder="Describe what this tool does..."
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                      />
                    </div>
                  </Tooltip>
                </div>
              </div>
              <div className="flex items-center justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsAddingTool(false)}
                  className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <Tooltip content="Create this tool and make it available to agents">
                  <button
                    type="submit"
                    className="px-6 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-xl hover:bg-primary-700 transition-colors flex items-center"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Create Tool
                  </button>
                </Tooltip>
              </div>
            </form>
          </div>
        )}

        {/* Tools Grid */}
        <div className="grid grid-cols-3 gap-4">
          {tools.map((tool) => (
            <div
              key={tool.id}
              className="bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Wrench className="w-5 h-5 text-orange-600" />
                </div>
                <StatusBadge status={tool.status} />
              </div>
              <h3 className="text-sm font-semibold text-gray-900 mb-1">{tool.name}</h3>
              <p className="text-xs text-gray-600 mb-3">{tool.description}</p>
              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <Tooltip content={`This tool has been used ${tool.usageCount.toLocaleString()} times by agents`}>
                  <span className="text-xs text-gray-500">
                    {tool.usageCount.toLocaleString()} uses
                  </span>
                </Tooltip>
                <div className="flex items-center space-x-1">
                  <Tooltip content="Edit tool configuration">
                    <button className="p-1.5 text-primary-600 hover:bg-primary-50 rounded transition-colors">
                      <Edit className="w-4 h-4" />
                    </button>
                  </Tooltip>
                  <Tooltip content="Delete this tool">
                    <button className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </Tooltip>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* MCP Servers Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Server className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">MCP Servers (7 Active)</h2>
              <p className="text-sm text-gray-600">External data sources and service connections for RAG pipeline</p>
            </div>
          </div>
          <Tooltip content="Add a new MCP server to expand knowledge sources">
            <button
              onClick={() => setIsAddingServer(true)}
              className="btn-primary flex items-center text-sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Server
            </button>
          </Tooltip>
        </div>

        {/* Server Categories */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {serverCategories.map((category) => (
            <Tooltip key={category.id} content={`${category.description} - ${category.count} server${category.count !== 1 ? 's' : ''} configured`}>
              <div className="p-4 rounded-xl border-2 bg-gradient-to-br from-gray-50 to-white border-gray-200 hover:border-primary-300 transition-all cursor-pointer">
                <div className="inline-flex p-2 bg-primary-100 rounded-lg mb-3">
                  {category.icon}
                </div>
                <div className="flex items-baseline justify-between mb-1">
                  <h3 className="text-sm font-semibold text-gray-900">{category.name}</h3>
                  <span className="text-lg font-bold text-primary-600">{category.count}</span>
                </div>
                <p className="text-xs text-gray-600">{category.description}</p>
              </div>
            </Tooltip>
          ))}
        </div>

        {/* Add Server Form */}
        {isAddingServer && (
          <div className="mb-6 bg-gradient-to-r from-primary-50 to-blue-50 rounded-xl border border-primary-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-primary-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Plus className="w-5 h-5 text-primary-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Add MCP Server</h3>
                </div>
                <Tooltip content="Close form">
                  <button
                    onClick={() => setIsAddingServer(false)}
                    className="p-2 hover:bg-white/50 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </Tooltip>
              </div>
            </div>
            <form className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Tooltip content="Friendly name for this MCP server">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Server Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., PostgreSQL Database"
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                    />
                  </div>
                </Tooltip>
                <Tooltip content="Type of knowledge source this server provides">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <select className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all">
                      <option value="local">Local Data Servers</option>
                      <option value="search">Search Servers</option>
                      <option value="cloud">Cloud Servers</option>
                    </select>
                  </div>
                </Tooltip>
                <Tooltip content="URL or connection string for this server">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Endpoint <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., localhost:5432"
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                    />
                  </div>
                </Tooltip>
                <Tooltip content="API key or credentials for authentication (stored securely)">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      API Key / Credentials
                    </label>
                    <div className="relative">
                      <input
                        type={showSecrets ? 'text' : 'password'}
                        placeholder="••••••••••••"
                        className="w-full px-4 py-3 pr-10 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all font-mono text-sm"
                      />
                      <Key className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                </Tooltip>
              </div>
              <div className="flex items-center justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsAddingServer(false)}
                  className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <Tooltip content="Add this server to the MCP configuration">
                  <button
                    type="submit"
                    className="px-6 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-xl hover:bg-primary-700 transition-colors flex items-center"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Add Server
                  </button>
                </Tooltip>
              </div>
            </form>
          </div>
        )}

        {/* MCP Servers List */}
        <div className="space-y-3">
          {mcpServers.map((server) => (
            <div
              key={server.id}
              className="flex items-start justify-between p-5 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-200 hover:shadow-md transition-all"
            >
              <div className="flex items-start space-x-4 flex-1">
                <div className="text-4xl mt-1">{server.icon}</div>
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-base font-semibold text-gray-900">{server.name}</h3>
                    <Tooltip content={`Category: ${server.category}`}>
                      <span className="px-2.5 py-0.5 bg-orange-50 text-[#d66a00] text-xs font-medium rounded border border-orange-200">
                        {server.category}
                      </span>
                    </Tooltip>
                    <StatusBadge status={server.status} />
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{server.description}</p>
                  <div className="flex items-center space-x-6 text-xs">
                    <Tooltip content="Server endpoint URL">
                      <div className="flex items-center space-x-1.5 text-gray-600">
                        <Server className="w-3.5 h-3.5 text-gray-400" />
                        <span className="font-mono">{server.endpoint}</span>
                      </div>
                    </Tooltip>
                    <Tooltip content="Reranking weight: Higher values (closer to 1.0) indicate more trusted sources in the hybrid ranking system">
                      <div className="flex items-center space-x-1.5">
                        <span className="text-gray-500">Weight:</span>
                        <span className="font-semibold text-gray-900">{server.weight}</span>
                      </div>
                    </Tooltip>
                    <Tooltip content="Average response time for queries to this server">
                      <div className="flex items-center space-x-1.5">
                        <span className="text-gray-500">Latency:</span>
                        <span className="font-semibold text-gray-900">{server.avgLatency}</span>
                      </div>
                    </Tooltip>
                    <Tooltip content="Server uptime percentage over the last 30 days">
                      <div className="flex items-center space-x-1.5">
                        <span className="text-gray-500">Uptime:</span>
                        <span className="font-semibold text-green-700">{server.uptime}</span>
                      </div>
                    </Tooltip>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2 ml-4">
                <Tooltip content="Test connection and measure current latency">
                  <button
                    onClick={() => handleTestConnection(server.id, server.name)}
                    disabled={testingServer === server.id}
                    className="px-3 py-1.5 text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg transition-colors border border-blue-200 disabled:bg-gray-100 disabled:text-gray-400 flex items-center"
                  >
                    {testingServer === server.id ? (
                      <>
                        <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                        Testing...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Test
                      </>
                    )}
                  </button>
                </Tooltip>
                <Tooltip content="Edit server configuration, credentials, and weight">
                  <button
                    onClick={() => setEditingServer(server.id)}
                    className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors border border-transparent hover:border-primary-200"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                </Tooltip>
                <Tooltip content="Remove this server from MCP configuration">
                  <button
                    onClick={() => handleDeleteServer(server.name)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-200"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </Tooltip>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Edit Server Modal */}
      {editingServer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-primary-50 to-blue-50 border-b border-primary-100 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Edit className="w-5 h-5 text-primary-600" />
                <h3 className="text-lg font-semibold text-gray-900">Edit MCP Server</h3>
              </div>
              <Tooltip content="Close without saving">
                <button
                  onClick={() => setEditingServer(null)}
                  className="p-2 hover:bg-white/50 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </Tooltip>
            </div>
            <form className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Tooltip content="Display name for this server">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Server Name
                    </label>
                    <input
                      type="text"
                      defaultValue={mcpServers.find(s => s.id === editingServer)?.name}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                    />
                  </div>
                </Tooltip>
                <Tooltip content="Server connection endpoint">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Endpoint
                    </label>
                    <input
                      type="text"
                      defaultValue={mcpServers.find(s => s.id === editingServer)?.endpoint}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                    />
                  </div>
                </Tooltip>
                <Tooltip content="Reranking weight (0.0 = lowest trust, 1.0 = highest trust). Controls how much to favor results from this source during hybrid reranking.">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Weight (0.0 - 1.0)
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      defaultValue={mcpServers.find(s => s.id === editingServer)?.weight}
                      className="w-full"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Current: {mcpServers.find(s => s.id === editingServer)?.weight}
                    </p>
                  </div>
                </Tooltip>
                <Tooltip content="API key or credentials (stored encrypted)">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      API Key / Credentials
                    </label>
                    <div className="relative">
                      <input
                        type={showSecrets ? 'text' : 'password'}
                        placeholder="••••••••••••"
                        className="w-full px-4 py-3 pr-10 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all font-mono text-sm"
                      />
                      <Tooltip content={showSecrets ? "Hide credentials" : "Show credentials"}>
                        <button
                          type="button"
                          onClick={() => setShowSecrets(!showSecrets)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showSecrets ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                </Tooltip>
              </div>
              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setEditingServer(null)}
                  className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <Tooltip content="Save changes and update server configuration">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingServer(null);
                      showToast('success', 'Server Updated', 'Server configuration updated successfully!');
                    }}
                    className="px-6 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-xl hover:bg-primary-700 transition-colors flex items-center"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </button>
                </Tooltip>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
